/**
 * RelatedTabsRail — rail vertical de íconos con badge de conteo para el panel
 * contextual de la Vista 360. En móvil colapsa a fila horizontal (lo maneja el
 * contenedor; el componente solo aplica `flex-col` por defecto).
 */
import type { ReactNode } from 'react'

export interface RelatedTab {
  key: string
  icon: ReactNode
  /** Tooltip / aria-label. */
  title: string
  /** Badge de conteo (se oculta si es 0 o undefined). */
  count?: number
}

interface RelatedTabsRailProps {
  tabs: RelatedTab[]
  activeKey: string
  onChange: (key: string) => void
  className?: string
}

export function RelatedTabsRail({ tabs, activeKey, onChange, className = '' }: RelatedTabsRailProps) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`} role="tablist" aria-orientation="vertical">
      {tabs.map((tab) => {
        const active = tab.key === activeKey
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={tab.title}
            title={tab.title}
            onClick={() => onChange(tab.key)}
            className={[
              'relative w-12 h-12 rounded-xl border grid place-items-center text-lg shadow-sm transition-colors',
              active
                ? 'bg-primary border-primary text-on-primary'
                : 'bg-surface border-divider text-content-muted hover:text-content hover:border-divider-strong',
            ].join(' ')}
          >
            {tab.icon}
            {tab.count != null && tab.count > 0 && (
              <span className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold leading-none flex items-center justify-center ring-2 ring-canvas">
                {tab.count > 99 ? '99+' : tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
